import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import httpClient from '../httpClient.ts';

// Types
export interface NotificationRule {
    id: string;
    name: string;
    trigger: string;
    template_id: string;
    recipients: string[];
    is_active: boolean;
    last_triggered_at: string | null;
    trigger_count: number;
    created_by: string | null;
    metadata: Record<string, any> | null;
    created_at: string;
    updated_at: string;
    template?: {
        id: string;
        name: string;
        type: string;
        title: string;
    };
}

export interface CreateNotificationRuleData {
    name: string;
    trigger: string;
    template_id: string;
    recipients: string[];
    is_active?: boolean;
    metadata?: Record<string, any>;
}

export interface UpdateNotificationRuleData {
    name?: string;
    trigger?: string;
    template_id?: string;
    recipients?: string[];
    is_active?: boolean;
    metadata?: Record<string, any>;
}

export interface RuleTestData {
    data: Record<string, any>;
}

export interface RuleSimulationData {
    trigger: string;
    data: Record<string, any>;
}

export interface RuleAnalytics {
    total_rules: number;
    active_rules: number;
    recently_triggered: number;
    rules_by_trigger: Record<string, number>;
    most_active_rules: NotificationRule[];
    trigger_activity: Array<{
        trigger: string;
        triggers: number;
        total_triggers: number;
    }>;
    period_hours: number;
}

// Hooks
export const useNotificationRules = (
    filters: {
        trigger?: string;
        active_only?: boolean;
        search?: string;
        sort_by?: string;
        sort_order?: 'asc' | 'desc';
        per_page?: number;
        page?: number;
    } = {}
) => {
    return useQuery<PaginatedResponse<NotificationRule>>({
        queryKey: ['notification-rules', filters],
        queryFn: async () => {
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    params.append(key, value.toString());
                }
            });

            const response = await httpClient.get(`/crm/notification-rules?${params}`);
            return response.data;
        },
    });
};

export const useNotificationRule = (id: string) => {
    return useQuery({
        queryKey: ['notification-rule', id],
        queryFn: async () => {
            const response = await httpClient.get(`/crm/notification-rules/${id}`);
            return response.data;
        },
        enabled: !!id,
    });
};

export const useCreateNotificationRule = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: CreateNotificationRuleData) => {
            const response = await httpClient.post('/crm/notification-rules', data);
            return response.data;
        },
        onSuccess: () => {
            // Invalidate all notification-rules queries (with and without filters)
            queryClient.invalidateQueries({
                predicate: query =>
                    Array.isArray(query.queryKey) && query.queryKey[0] === 'notification-rules',
            });
            queryClient.invalidateQueries({ queryKey: ['notification-rule-analytics'] });
        },
    });
};

export const useUpdateNotificationRule = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            id,
            data,
        }: {
            id: string;
            data: Partial<CreateNotificationRuleData>;
        }) => {
            if (!id || id.trim() === '') {
                throw new Error('Invalid rule ID');
            }

            const response = await httpClient.put(`/crm/notification-rules/${id}`, data);
            return response.data;
        },
        onSuccess: (_, { id }) => {
            // Invalidate all notification-rules queries (with and without filters)
            queryClient.invalidateQueries({
                predicate: query =>
                    Array.isArray(query.queryKey) && query.queryKey[0] === 'notification-rules',
            });
            queryClient.invalidateQueries({ queryKey: ['notification-rule', id] });
        },
    });
};

export const useDeleteNotificationRule = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            if (!id || id.trim() === '') {
                throw new Error('Invalid rule ID');
            }

            const response = await httpClient.delete(`/crm/notification-rules/${id}`);
            return response.data;
        },
        onSuccess: () => {
            // Invalidate all notification-rules queries (with and without filters)
            queryClient.invalidateQueries({
                predicate: query =>
                    Array.isArray(query.queryKey) && query.queryKey[0] === 'notification-rules',
            });
        },
    });
};

export const useTestNotificationRule = () => {
    return useMutation({
        mutationFn: async ({ id, data }: { id: string; data: RuleTestData }) => {
            if (!id || id.trim() === '') {
                throw new Error('Invalid rule ID');
            }

            const response = await httpClient.post(`/crm/notification-rules/${id}/test`, data);
            return response.data;
        },
    });
};

export const useToggleNotificationRule = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            if (!id || id.trim() === '') {
                throw new Error('Invalid rule ID');
            }

            const response = await httpClient.post(`/crm/notification-rules/${id}/toggle`);
            return response.data;
        },
        onSuccess: (_, id) => {
            // Invalidate all notification-rules queries (with and without filters)
            queryClient.invalidateQueries({
                predicate: query =>
                    Array.isArray(query.queryKey) && query.queryKey[0] === 'notification-rules',
            });
            queryClient.invalidateQueries({ queryKey: ['notification-rule', id] });
            queryClient.invalidateQueries({ queryKey: ['notification-rule-analytics'] });
        },
    });
};

export const useAvailableTriggers = () => {
    return useQuery({
        queryKey: ['available-triggers'],
        queryFn: async () => {
            try {
                const response = await httpClient.get('/crm/notification-rules/triggers/available');
                return response.data;
            } catch (error) {
                console.error('Error fetching available triggers:', error);
                throw error;
            }
        },
        staleTime: 1000 * 60 * 60, // 1 hour - triggers don't change often
        retry: 3,
        retryDelay: 1000,
    });
};

export const useNotificationRuleAnalytics = (hours: number = 24) => {
    return useQuery({
        queryKey: ['notification-rule-analytics', hours],
        queryFn: async (): Promise<RuleAnalytics> => {
            const response = await httpClient.get(
                `/crm/notification-rules/analytics/overview?hours=${hours}`
            );
            return response.data;
        },
    });
};

export const useSimulateNotificationRules = () => {
    return useMutation({
        mutationFn: async (data: RuleSimulationData) => {
            const response = await httpClient.post('/crm/notification-rules/simulate', data);
            return response.data;
        },
    });
};

export const getRecipientOptions = (): Array<{ value: string; label: string }> => {
    return [
        { value: 'customer', label: 'Customer' },
        { value: 'compliance-team', label: 'Compliance Team' },
        { value: 'risk-team', label: 'Risk Management Team' },
        { value: 'finance-team', label: 'Finance Team' },
        { value: 'support-team', label: 'Support Team' },
        { value: 'management', label: 'Management' },
        { value: 'all-staff', label: 'All Staff' },
    ];
};
